import { useState, useEffect } from 'react';
import { FaShieldAlt, FaCopy, FaCheck } from 'react-icons/fa';
import { getFreshAccessToken } from '../../../utils/authClient';

// Connector credentials for Claude (§5.2.3).
//
// Nothing is fetched when this opens. The credentials endpoint calls
// DescribeUserPoolClient, which shares a 15 RPS account-wide, non-adjustable
// bucket, and more to the point a secret should not be sitting in a response
// nobody asked for. It is fetched once, on Reveal, behind step-up re-auth
// (§5.2.2.1 rule 4).
//
// The client id is derived server-side from the caller's own identity and is
// never sent from here. There is deliberately no way to ask for anyone else's.
const ConnectClaudeModal = ({ isOpen, onClose, apiUrl, mfaEnabled, mfaStatusFailed, onSetUpMfa }) => {
    const [password, setPassword] = useState('');
    const [code, setCode] = useState('');
    const [creds, setCreds] = useState(null);
    const [error, setError] = useState('');
    const [busy, setBusy] = useState(false);
    const [copied, setCopied] = useState('');

    const SESSION_EXPIRED_MSG = 'Your session has expired. Please log out and back in, then retry.';

    const authHeaders = async () => {
        const accessToken = await getFreshAccessToken();
        if (!accessToken) throw new Error('SESSION_EXPIRED');
        return {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`
        };
    };

    // Clear the secret whenever the modal closes or reopens. Leaving it in
    // component state after close would keep it one render away from a screen
    // the user thinks they have dismissed.
    useEffect(() => {
        setPassword('');
        setCode('');
        setCreds(null);
        setError('');
        setCopied('');
    }, [isOpen]);

    if (!isOpen) return null;

    const copy = async (value, label) => {
        try {
            await navigator.clipboard.writeText(value);
            setCopied(label);
            setTimeout(() => setCopied(''), 2000);
        } catch {
            setError('Could not copy to the clipboard - select the text and copy manually.');
        }
    };

    const reveal = async (e) => {
        e.preventDefault();
        setError('');
        setBusy(true);
        try {
            const res = await fetch(`${apiUrl}/client/mcp/credentials`, {
                method: 'POST',
                headers: await authHeaders(),
                body: JSON.stringify({ password, code })
            });
            const data = await res.json();
            if (!res.ok) {
                setError(data.message || data.error || 'Could not retrieve your connector credentials.');
                return;
            }
            setCreds(data);
            // The password and code have done their job; keep neither.
            setPassword('');
            setCode('');
        } catch (err) {
            setError(err.message === 'SESSION_EXPIRED'
                ? SESSION_EXPIRED_MSG
                : 'Could not retrieve your connector credentials.');
        } finally {
            setBusy(false);
        }
    };

    const CopyRow = ({ label, value, name }) => (
        <div className="mb-3">
            <div className="text-xs font-medium text-gray-500 mb-1">{label}</div>
            <div className="flex items-center gap-2">
                <code className="flex-1 text-xs bg-gray-100 border border-gray-200 rounded px-2 py-1.5 break-all">
                    {value}
                </code>
                <button
                    type="button"
                    onClick={() => copy(value, name)}
                    className="text-sm px-2 py-1.5 bg-gray-100 text-gray-600 border border-gray-300 rounded hover:bg-gray-200"
                    title={`Copy ${label.toLowerCase()}`}
                >
                    {copied === name ? <FaCheck size={12} className="text-green-600" /> : <FaCopy size={12} />}
                </button>
            </div>
        </div>
    );

    // MFA gate. A connector grant is long-lived and off-device, so §9.9 requires
    // a second factor even though the portal keeps MFA optional. Blocking here
    // rather than at the endpoint means nobody ends up holding a credential
    // that cannot work, wondering why.
    const mfaBlocked = mfaEnabled === false;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
                <div className="flex items-center justify-between px-5 py-3 border-b">
                    <h2 className="text-lg font-semibold text-gray-800">Connect Claude</h2>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none">&times;</button>
                </div>

                <div className="p-5">
                    {mfaBlocked ? (
                        <div className="text-center py-4">
                            <FaShieldAlt size={28} className="text-amber-500 mx-auto mb-3" />
                            <h3 className="font-semibold text-gray-800 mb-2">
                                Set up two-factor authentication first
                            </h3>
                            <p className="text-sm text-gray-600 mb-4">
                                Connecting Claude gives it ongoing access to this project from outside
                                the portal, so it needs a second factor on your account.
                            </p>
                            <button onClick={onSetUpMfa} className="btn-primary text-sm px-4 py-2">
                                Set up now
                            </button>
                        </div>
                    ) : (
                        <>
                            <p className="text-sm text-gray-600 mb-3">
                                Ask Claude about this project from your phone or laptop — your tasks,
                                dates, risks and comments — without opening the portal.
                            </p>
                            <p className="text-sm text-gray-600 mb-4">
                                In Claude: Settings → Connectors → Add custom connector, then paste these.
                            </p>

                            {mfaEnabled === null && (
                                <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2 mb-4">
                                    {mfaStatusFailed
                                        ? 'Could not check your two-factor status. You can still continue; the server will refuse if it is not set up.'
                                        : 'Checking your two-factor status…'}
                                </div>
                            )}

                            {creds ? (
                                <>
                                    <CopyRow label="Server URL" value={creds.serverUrl} name="url" />
                                    <CopyRow label="Client ID" value={creds.clientId} name="id" />
                                    <CopyRow label="Client secret" value={creds.clientSecret} name="secret" />
                                    <p className="text-xs text-gray-600 mt-4">
                                        Claude can do what you can do in the portal — read the project, and
                                        change tasks, comments, risks and issues. It cannot manage users.
                                    </p>
                                    <p className="text-xs text-gray-600 mt-2">
                                        Lost the secret? It can&apos;t be shown again or reset — we replace it,
                                        and you re-add the connector.
                                    </p>
                                </>
                            ) : (
                                <form onSubmit={reveal}>
                                    <p className="text-sm text-gray-700 mb-3">
                                        Confirm it&apos;s you, and your credentials will be shown.
                                    </p>
                                    <input
                                        type="password"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        placeholder="Your password"
                                        autoComplete="current-password"
                                        className="w-full border border-gray-300 rounded px-3 py-2 text-sm mb-2"
                                        required
                                    />
                                    <input
                                        type="text"
                                        value={code}
                                        onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                                        placeholder="Authenticator code"
                                        inputMode="numeric"
                                        maxLength={6}
                                        autoComplete="one-time-code"
                                        className="w-full border border-gray-300 rounded px-3 py-2 text-sm mb-3"
                                        required
                                    />
                                    <button
                                        type="submit"
                                        disabled={busy || !password || code.length !== 6}
                                        className="btn-primary text-sm px-4 py-2 disabled:opacity-50"
                                    >
                                        {busy ? 'Checking…' : 'Reveal'}
                                    </button>
                                </form>
                            )}
                        </>
                    )}

                    {error && (
                        <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2 mt-4">
                            {error}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ConnectClaudeModal;
